import type React from "react";

/**
 * Theme cho template Golden Pen (`mrw_template_4`, trang `/kichhoat`).
 *
 * Nguồn dữ liệu là `template.data` trong response `GET /check` và `GET /landing-config`
 * — chính là cột `layout_templates.data` mà admin sửa ở màn "Mẫu trang xác thực"
 * (đã gộp customization của company).
 *
 * Mặc định dưới đây = ĐÚNG những gì giao diện đang render trước khi có tính năng này,
 * nên khi admin chưa cấu hình gì (hoặc backend chưa migrate) trang không đổi một pixel.
 * Mỗi lần sửa mặc định ở đây phải sửa kèm `App\Constants\LandingTemplateData` bên backend.
 */
export const TEMPLATE_FOUR_DEFAULTS = {
  // --- Màu ---
  text_default: "#000000",
  color_success: "#11970E",
  color_warring: "#D97706",
  color_error: "#FF4D4F",
  page_bg: "#E5E5E5",
  heading_bg: "#FEFBD9",
  activate_button_bg: "#FF4D4F",
  activate_button_text: "#FFFFFF",
  link_color: "#FB2C36",
  card_code_color: "#D4380D",
  muted_text_color: "#AEADAD",
  footer_text_color: "#585454",
  info_row_bg: "#F9FAFB",
  info_label_color: "#6A7282",
  info_value_color: "#101828",
  note_text_color: "#4A5565",

  // --- Nội dung ---
  logo: "",
  heading_text: "Kích hoạt bảo hành sản phẩm",
  success_title: "Xin chúc mừng!",
  success_subtitle: "Bạn đã mua được sản phẩm {company} chính hãng.",
  activate_prompt: "Xin vui lòng điền thông tin để kích hoạt bảo hành sản phẩm.",
  activate_button_label: "Gửi thông tin",
  manual_activate_button_label: "Kích hoạt bảo hành",
  thankyou_text: "Trân trọng cảm ơn Quý khách đã tin cậy lựa chọn, sử dụng sản phẩm của",
  manual_thankyou_text: "Trân trọng cảm ơn Quý khách đã tin cậy lựa chọn, sử dụng sản phẩm máy tính của",
  manual_brand_link_label: "Canon!",
  manual_brand_link_url: "https://vn.canon",
  footer_text: "Nhập khẩu và phân phối độc quyền bới Công ty Cổ phần Golden Pen",
  hotline: "1900.9486",
  // Nhãn dòng "Trạng thái": tem CŨ khách trả lại vs tem MỚI nhận về sau bảo hành.
  // Lời nhắn kèm theo của trạng thái "đổi mới" cấu hình ở scan-setting
  // (khối `stamp_warranty_exchanged`) và về theo `config`/`message`, không nằm ở đây.
  exchanged_status_label: "Hàng bảo hành trả lại",
  new_exchange_status_label: "Hàng bảo hành đổi mới",
} as const;

export type TemplateFourKey = keyof typeof TEMPLATE_FOUR_DEFAULTS;

/** Các khoá được đẩy ra CSS variable (`--t4-*`) để stylesheet dùng. */
const CSS_VAR_KEYS: TemplateFourKey[] = [
  "text_default",
  "color_success",
  "color_warring",
  "color_error",
  "page_bg",
  "heading_bg",
  "activate_button_bg",
  "activate_button_text",
  "link_color",
  "card_code_color",
  "muted_text_color",
  "footer_text_color",
  "info_row_bg",
  "info_label_color",
  "info_value_color",
  "note_text_color",
];

export interface TemplateFourTheme {
  /** Gắn lên phần tử bọc ngoài `.baohanh-barcode`. */
  vars: React.CSSProperties;
  /** Giá trị một khoá, tự lùi về mặc định khi admin để trống. */
  get: (key: TemplateFourKey) => string;
  /**
   * Nút kích hoạt có được đổi màu không.
   *
   * Khi KHÔNG đổi, nút giữ nguyên `danger` của antd để mọi trạng thái (hover/active/
   * disabled) y hệt trước đây — CSS override chỉ bật khi admin thực sự chọn màu khác,
   * vì override thủ công không tái tạo được đúng dải màu antd tự suy ra.
   */
  hasCustomActivateButton: boolean;
  /**
   * Màu lỗi có được đổi khác mặc định không — dùng cho nút "Trở về lại" ở khối cảnh
   * báo/lỗi. Cùng lý do như `hasCustomActivateButton`: không đổi thì để antd lo.
   */
  hasCustomErrorColor: boolean;
}

const normalize = (value?: string | null): string => (typeof value === "string" ? value.trim() : "");

const cssVarName = (key: TemplateFourKey): string => `--t4-${key.replace(/_/g, "-")}`;

export const buildTemplateFourTheme = (data?: Record<string, string> | null): TemplateFourTheme => {
  const get = (key: TemplateFourKey): string => normalize(data?.[key]) || TEMPLATE_FOUR_DEFAULTS[key];

  const vars: Record<string, string> = {};
  CSS_VAR_KEYS.forEach((key) => {
    vars[cssVarName(key)] = get(key);
  });

  const activateBg = get("activate_button_bg").toUpperCase();
  const activateText = get("activate_button_text").toUpperCase();

  return {
    vars: vars as React.CSSProperties,
    get,
    hasCustomActivateButton:
      activateBg !== TEMPLATE_FOUR_DEFAULTS.activate_button_bg ||
      activateText !== TEMPLATE_FOUR_DEFAULTS.activate_button_text,
    hasCustomErrorColor: get("color_error").toUpperCase() !== TEMPLATE_FOUR_DEFAULTS.color_error,
  };
};

/**
 * Tách `{company}` trong câu chúc mừng thành 3 phần để chèn tên công ty in đậm.
 * Trả về chuỗi thuần — KHÔNG bao giờ render bằng `dangerouslySetInnerHTML`, nếu không
 * tên công ty (dữ liệu) sẽ trở thành đường tiêm mã.
 */
export const splitCompanyPlaceholder = (template: string): [string, string] => {
  const marker = "{company}";
  const index = template.indexOf(marker);
  if (index === -1) {
    return [template, ""];
  }

  return [template.slice(0, index), template.slice(index + marker.length)];
};
